import { useCallback, useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import { useAuth } from "../../../core/platform/auth/AuthProvider";
import { refreshInventoryReplica } from "../replica";
import { inventoryApi, type Alerts } from "./api";
import { InventoryManager } from "./InventoryManager";

/**
 * The till-header "Stock" button (T021, FR-072, SC-023).
 *
 * IN `terminal.actions` BECAUSE THAT IS WHAT CORE RENDERS. The back-office
 * slots are declared in the registry but drawn nowhere; the header bar is
 * where Sales, Menu and Settings already live, on the tablet the café actually
 * uses. The button shares `.terminal__signout`'s pill so it reads as one of
 * them.
 *
 * THE BADGE IS THE ALERT. Whoever can see stock sees, without opening
 * anything, how many ingredients need attention — red when any is negative,
 * which is a number to correct rather than a shortage to reorder (FR-119). It
 * refreshes every minute and whenever the screens close.
 *
 * THE REPLICA REFRESH RUNS FOR EVERYONE. A cashier never sees this button, but
 * the till they are ringing on still deducts, so it still needs the current
 * recipes. That is why this component mounts for every signed-in person and
 * only then decides whether to draw anything.
 */

const REFRESH_MS = 60_000;

export function InventoryButton({ token }: SlotProps) {
  const { can } = useAuth();
  const sessionToken = typeof token === "string" ? token : null;
  const canView = can("inventory.view");

  const [alerts, setAlerts] = useState<Alerts | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sessionToken) return;
    void refreshInventoryReplica(sessionToken).catch(() => false);
  }, [sessionToken]);

  const refreshAlerts = useCallback(async () => {
    if (!sessionToken || !canView) return;
    try {
      setAlerts(await inventoryApi(sessionToken).alerts());
    } catch {
      // Offline or refused: keep the last known figure rather than blanking
      // a warning that is probably still true.
    }
  }, [sessionToken, canView]);

  useEffect(() => {
    void refreshAlerts();
    const timer = setInterval(() => void refreshAlerts(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [refreshAlerts]);

  if (!sessionToken || !canView) return null;

  const attention = alerts?.attention ?? [];
  const negative = attention.filter((ingredient) => ingredient.state === "negative").length;
  const summary =
    attention.length === 0
      ? "Stock"
      : `${attention.length} ingredient${attention.length === 1 ? "" : "s"} need${attention.length === 1 ? "s" : ""} attention` +
        (negative > 0 ? ` — ${negative} below zero` : "");

  return (
    <>
      <button
        type="button"
        className="terminal__signout inv__button"
        onClick={() => setOpen(true)}
        aria-label={summary}
      >
        Stock
        {attention.length > 0 && (
          <span className={`inv__badge${negative > 0 ? " is-negative" : ""}`} role="status" title={summary}>
            {attention.length}
          </span>
        )}
      </button>

      {open && (
        <div className="till__overlay">
          <InventoryManager
            token={sessionToken}
            alerts={alerts}
            onChanged={refreshAlerts}
            onClose={() => {
              setOpen(false);
              void refreshAlerts();
              // A recipe edited just now must reach this till's next sale.
              void refreshInventoryReplica(sessionToken).catch(() => false);
            }}
          />
        </div>
      )}
    </>
  );
}

export default InventoryButton;
